import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_API_URL}/event-surveys`;

// ─── Types ──────────────────────────────────────────────

interface Question {
  id: number;
  question_text: string;
  question_type: string;
  options: string[];
  is_required: boolean;
  step_number: number;
  sort_order: number;
  condition: { question_id: number; operator: string; value: string | string[] } | null;
  settings: any;
}

interface Survey {
  id: number;
  slug: string;
  title: string;
  description: string;
  event_name: string;
  event_date: string;
  collect_contact_info: boolean;
  welcome_title: string;
  welcome_message: string;
  thank_you_title: string;
  thank_you_message: string;
  questions: Question[];
  already_submitted: boolean;
  prefill_name?: string | null;
  prefill_email?: string | null;
  settings?: {
    calendar?: {
      title?: string;
      description?: string;
      location?: string;
      start: string;
      end: string;
      show_when?: { question_id: number; value: string } | null;
      send_email?: boolean;
    } | null;
  } | null;
}

// ─── Fingerprint (basit tarayıcı parmak izi) ───────────
const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Cihaza özel RASTGELE kimlik ('v2-' öneki = backend için güvenilir). Eski türetilmiş parmak izi
// (userAgent+ekran+dil) aynı model telefon + aynı iOS sürümünde çakışıyor ve ikinci katılımcıyı
// "zaten doldurdunuz" sayıp teşekkür ekranına düşürüyordu (17 Eki 2026 Developer Summit standı).
// localStorage'da anket bazında saklanır: aynı cihazdan tekrar doldurma yine engellenir.
function getDeviceId(slugKey: string): string {
  const key = `survey_device_${slugKey}`;
  const rnd = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? (crypto as any).randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36) + Math.random().toString(36).slice(2);
  if (isDev) return `v2-dev-${rnd()}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing && existing.startsWith('v2-')) return existing;
    const fresh = `v2-${rnd()}`;
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    // Gizli sekme / depolama kapalı: oturuma özel rastgele kimlik (tekrar doldurma engeli yok, kayıp yok)
    return `v2-${rnd()}`;
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function generateFingerprint(): string {
  const nav = window.navigator;
  const screen = window.screen;
  const raw = [
    nav.userAgent,
    nav.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    nav.hardwareConcurrency || '',
    (nav as any).deviceMemory || '',
    // Dev ortamında her oturumda farklı fingerprint → tekrarlı test
    isDev ? Date.now().toString() : '',
  ].join('|');

  // Multi-round hash for reliable length
  let h1 = 0;
  let h2 = 0x9e3779b9;
  for (let i = 0; i < raw.length; i++) {
    const c = raw.charCodeAt(i);
    h1 = ((h1 << 5) - h1 + c) | 0;
    h2 = ((h2 << 7) ^ (h2 >>> 3) ^ c) | 0;
  }
  const p1 = Math.abs(h1).toString(36).padStart(7, '0');
  const p2 = Math.abs(h2).toString(36).padStart(7, '0');
  const p3 = raw.length.toString(36).padStart(2, '0');
  return (p1 + p2 + p3).slice(0, 20);
}

// ─── Corporate Colors ───────────────────────────────────
const BRAND = '#ed1c2a';
const BRAND_DARK = '#c41520';
const BRAND_LIGHT = '#fdebed';
const DARK = '#1a1a2e';

// ─── Global CSS (injected once) ─────────────────────────
const GLOBAL_CSS_ID = 'survey-page-styles';
function injectGlobalCSS() {
  if (document.getElementById(GLOBAL_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = GLOBAL_CSS_ID;
  style.textContent = `
    @keyframes surveyFadeIn {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes surveyPulse {
      0%, 100% { transform: scale(1); }
      50%      { transform: scale(1.05); }
    }
    @keyframes surveyShimmer {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes surveySpin {
      to { transform: rotate(360deg); }
    }
    .survey-fadeIn {
      animation: surveyFadeIn 0.45s cubic-bezier(.22,1,.36,1) both;
    }
    .survey-input:focus {
      border-color: ${BRAND} !important;
      box-shadow: 0 0 0 3px rgba(237,28,42,0.12) !important;
    }
    .survey-btn-primary:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(237,28,42,0.35);
    }
    .survey-btn-primary:active:not(:disabled) {
      transform: translateY(0);
    }
    .survey-btn-secondary:hover {
      background: #d0d5dd !important;
    }
    .survey-option:hover {
      border-color: ${BRAND} !important;
      background: ${BRAND_LIGHT} !important;
    }
    .survey-star:hover { transform: scale(1.25); }
    .survey-question-block {
      animation: surveyFadeIn 0.45s cubic-bezier(.22,1,.36,1) both;
    }
    .survey-question-block:nth-child(2) { animation-delay: 0.08s; }
    .survey-question-block:nth-child(3) { animation-delay: 0.16s; }
    .survey-question-block:nth-child(4) { animation-delay: 0.24s; }

    @media (max-width: 640px) {
      .survey-page-wrapper {
        padding: 0 !important;
        align-items: stretch !important;
      }
      .survey-page-container {
        border-radius: 0 !important;
        box-shadow: none !important;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }
      .survey-page-header {
        padding: 20px 20px 16px !important;
        border-radius: 0 !important;
      }
      .survey-page-content {
        padding: 20px !important;
        flex: 1;
      }
      .survey-page-footer {
        padding: 14px 20px !important;
      }
    }
  `;
  document.head.appendChild(style);
}

// ─── Styles ─────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: '100vh',
    background: `linear-gradient(160deg, ${DARK} 0%, #2d1b30 40%, #3a1520 100%)`,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '32px 16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    boxSizing: 'border-box' as const,
  },
  container: {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.05)',
    width: '100%',
    maxWidth: '660px',
    overflow: 'hidden',
    position: 'relative' as const,
  },
  header: {
    background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
    padding: '28px 32px 20px',
    color: '#ffffff',
    position: 'relative' as const,
    overflow: 'hidden',
  },
  headerDecor: {
    position: 'absolute' as const,
    top: '-30px',
    right: '-30px',
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.08)',
  },
  headerDecor2: {
    position: 'absolute' as const,
    bottom: '-20px',
    left: '40%',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
  },
  headerTitle: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#ffffff',
    margin: 0,
    marginBottom: '2px',
    position: 'relative' as const,
    zIndex: 1,
    letterSpacing: '-0.01em',
  },
  headerSub: {
    fontSize: '0.85rem',
    opacity: 0.85,
    margin: 0,
    position: 'relative' as const,
    zIndex: 1,
    fontWeight: 400,
  },
  progressWrap: {
    height: '4px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '4px',
    marginTop: '18px',
    overflow: 'hidden',
    position: 'relative' as const,
    zIndex: 1,
  },
  progressBar: {
    height: '100%',
    background: '#ffffff',
    borderRadius: '4px',
    transition: 'width 0.5s cubic-bezier(.22,1,.36,1)',
  },
  stepIndicator: {
    fontSize: '0.78rem',
    opacity: 0.75,
    marginTop: '8px',
    position: 'relative' as const,
    zIndex: 1,
    fontWeight: 500,
    letterSpacing: '0.03em',
  },
  content: {
    padding: '32px',
    minHeight: '280px',
  },
  footer: {
    padding: '16px 32px',
    background: '#fafbfc',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid #f0f0f4',
  },
  btnPrimary: {
    background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 30px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.25s cubic-bezier(.22,1,.36,1)',
    letterSpacing: '0.01em',
  },
  btnSecondary: {
    background: '#e8eaed',
    color: '#4a5568',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 26px',
    fontSize: '0.95rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  questionBlock: {
    marginBottom: '28px',
    padding: '20px',
    borderRadius: '12px',
    background: '#fafbfc',
    border: '1px solid #f0f0f4',
    transition: 'all 0.3s ease',
  },
  questionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1e2a3a',
    marginBottom: '14px',
    lineHeight: 1.5,
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: '2px solid #e2e6ea',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'all 0.25s ease',
    background: '#fff',
    color: '#1e2a3a',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: '2px solid #e2e6ea',
    fontSize: '0.95rem',
    outline: 'none',
    minHeight: '110px',
    resize: 'vertical' as const,
    transition: 'all 0.25s ease',
    background: '#fff',
    color: '#1e2a3a',
    boxSizing: 'border-box' as const,
  },
  optionBtn: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '13px 16px',
    borderRadius: '10px',
    border: '2px solid #e2e6ea',
    background: '#fff',
    fontSize: '0.95rem',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'all 0.2s ease',
    marginBottom: '8px',
    color: '#374151',
    gap: '10px',
  },
  optionBtnSelected: {
    borderColor: BRAND,
    background: BRAND_LIGHT,
    color: BRAND_DARK,
    fontWeight: 600,
  },
  optionRadio: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: `2px solid #cbd5e0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.2s ease',
  },
  optionRadioSelected: {
    borderColor: BRAND,
    background: BRAND,
  },
  optionCheck: {
    width: '18px',
    height: '18px',
    borderRadius: '5px',
    border: `2px solid #cbd5e0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.2s ease',
  },
  optionCheckSelected: {
    borderColor: BRAND,
    background: BRAND,
  },
  error: {
    color: BRAND,
    fontSize: '0.83rem',
    marginTop: '8px',
    fontWeight: 500,
  },
  centerText: {
    textAlign: 'center' as const,
    padding: '40px 20px',
  },
  poweredBy: {
    textAlign: 'center' as const,
    padding: '14px',
    borderTop: '1px solid #f0f0f4',
  },
};

// ─── E-posta yardımcıları (iletişim adımı) ────────────────────────────────
// Katılımcı e-postası gönderimi asla engellemez; yalnızca kırpılır/küçültülür ve geçersiz görünürse uyarı gösterilir.
const EMAIL_RE = /^[a-z0-9._%+'-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
const normalizeContactEmail = (raw: string) =>
  (raw || '')
    .trim()
    .replace(/^mailto:/i, '')
    .replace(/\s+/g, '')
    .replace(/[,;]+$/, '')
    .toLowerCase();
const emailLooksValid = (v: string) => !v || EMAIL_RE.test(v);
// Sunucudan gelen Joi/İngilizce doğrulama metinlerini katılımcıya göstermeyelim
const friendlySubmitError = (msg?: string) =>
  msg && !/must be|is required|is not allowed|fails to match|\"/.test(msg) ? msg : 'Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.';

export default function PublicSurveyPage({ inviteToken }: { inviteToken?: string } = {}) {
  const { slug } = useParams<{ slug: string }>();

  // Inject global CSS once
  useEffect(() => {
    injectGlobalCSS();
  }, []);

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(0); // 0 = welcome, -1 = thank you
  const [answers, setAnswers] = useState<Record<number, { text?: string; json?: any }>>({});
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactEmailWarning, setContactEmailWarning] = useState(''); // yumuşak uyarı — gönderimi ENGELLEMEZ
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});

  const startTimeRef = useRef(Date.now());
  const fingerprintRef = useRef(getDeviceId(String(inviteToken || slug || '')));

  // ─── Iframe detection ─────────────────────────────────
  const isIframe = useMemo(() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }, []);

  // ─── Load survey ──────────────────────────────────────
  const loadSurvey = useCallback(async () => {
    try {
      setLoading(true);
      const res = inviteToken
        ? await axios.get(`${API}/public/invite/${inviteToken}`)
        : await axios.get(`${API}/public/${slug}`, { headers: { 'X-Fingerprint': fingerprintRef.current } });
      const data = res.data.data;
      setSurvey(data);
      // Davet modunda kişi bilgileri önceden doldurulur
      if (data.prefill_name) setContactName(data.prefill_name);
      if (data.prefill_email) setContactEmail(data.prefill_email);
      if (data.already_submitted && !isDev) {
        setCurrentStep(-1);
      }
    } catch {
      setError('Anket bulunamadı veya artık aktif değil.');
    } finally {
      setLoading(false);
    }
  }, [slug, inviteToken]);

  useEffect(() => {
    loadSurvey();
    // Sayfa başlığı
    document.title = 'Anket | Aynet Teknoloji';
  }, [loadSurvey]);

  // ─── Steps ────────────────────────────────────────────
  const stepNumbers = useMemo(() => {
    if (!survey) return [];
    return Array.from(new Set(survey.questions.map((q) => q.step_number))).sort((a, b) => a - b);
  }, [survey]);

  const totalSteps = stepNumbers.length + (survey?.collect_contact_info ? 1 : 0); // +1 for contact step if enabled

  // Condition check
  const isQuestionVisible = useCallback(
    (q: Question): boolean => {
      const visible = (question: Question, depth = 0): boolean => {
        if (!question.condition) return true;
        if (depth > 10) return true; // döngüsel koşul koruması
        // Bağlı olduğu soru gizliyse bu soru da gizlidir (zincir koşul)
        const dep = survey?.questions.find((x) => x.id === question.condition!.question_id);
        if (dep && !visible(dep, depth + 1)) return false;
        const depAnswer = answers[question.condition.question_id];
        if (!depAnswer) return false;
        const val = depAnswer.text || '';
        switch (question.condition.operator) {
          case 'equals':
            return val === question.condition.value;
          case 'not_equals':
            return val !== question.condition.value;
          case 'contains':
            return val.includes(question.condition.value as string);
          case 'in':
            return Array.isArray(question.condition.value) && question.condition.value.includes(val);
          default:
            return true;
        }
      };
      return visible(q);
    },
    [answers, survey],
  );

  // ─── Takvime ekle (settings.calendar; show_when koşulu sağlanınca) ───
  const calendar = survey?.settings?.calendar;
  const showCalendar = useMemo(() => {
    if (!calendar || !calendar.start || !calendar.end) return false;
    if (!calendar.show_when) return true;
    return (answers[calendar.show_when.question_id]?.text || '') === calendar.show_when.value;
  }, [calendar, answers]);
  const icsUtc = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  const icsEscape = (v?: string) => String(v || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
  const calendarTitle = calendar?.title || survey?.event_name || survey?.title || 'Etkinlik';
  const googleCalendarUrl = calendar
    ? 'https://calendar.google.com/calendar/render?action=TEMPLATE' +
      `&text=${encodeURIComponent(calendarTitle)}&dates=${icsUtc(calendar.start)}/${icsUtc(calendar.end)}` +
      `&details=${encodeURIComponent(calendar.description || '')}&location=${encodeURIComponent(calendar.location || '')}`
    : '#';
  const downloadIcs = () => {
    if (!calendar) return;
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Aynet CRM//Etkinlik//TR', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:survey-${survey?.id}-${Date.now()}@aynetteknoloji.com.tr`,
      `DTSTAMP:${icsUtc(new Date().toISOString())}`, `DTSTART:${icsUtc(calendar.start)}`, `DTEND:${icsUtc(calendar.end)}`,
      `SUMMARY:${icsEscape(calendarTitle)}`, `DESCRIPTION:${icsEscape(calendar.description)}`, `LOCATION:${icsEscape(calendar.location)}`,
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'etkinlik.ics';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const currentStepQuestions = useMemo(() => {
    if (!survey || currentStep <= 0 || currentStep > stepNumbers.length) return [];
    const stepNum = stepNumbers[currentStep - 1];
    return survey.questions.filter((q) => q.step_number === stepNum && isQuestionVisible(q));
  }, [survey, currentStep, stepNumbers, isQuestionVisible]);

  // ─── Validation ───────────────────────────────────────
  const validateCurrentStep = (): boolean => {
    const errors: Record<number, string> = {};
    for (const q of currentStepQuestions) {
      if (q.is_required) {
        const a = answers[q.id];
        const hasAnswer = a && ((a.text && a.text.trim()) || (a.json && (Array.isArray(a.json) ? a.json.length > 0 : true)));
        if (!hasAnswer) {
          errors[q.id] = 'Bu soru zorunludur';
        }
      }
      // Email validation
      if (q.question_type === 'email' && answers[q.id]?.text) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(answers[q.id].text!)) {
          errors[q.id] = 'Geçerli bir e-posta adresi girin';
        }
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ─── Navigation ───────────────────────────────────────
  const handleNext = () => {
    if (currentStep === 0) {
      // Welcome -> first step
      setCurrentStep(1);
      return;
    }

    if (!validateCurrentStep()) return;

    // Contact info step (after all question steps)
    if (currentStep === stepNumbers.length && survey?.collect_contact_info) {
      setCurrentStep(stepNumbers.length + 1);
      return;
    }

    // Last step -> submit
    if (currentStep === stepNumbers.length || (currentStep === stepNumbers.length + 1 && survey?.collect_contact_info)) {
      handleSubmit();
      return;
    }

    setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  // ─── Submit ───────────────────────────────────────────
  const handleSubmit = async () => {
    if (!survey) return;

    // Kalan validasyon
    if (currentStep > 0 && currentStep <= stepNumbers.length && !validateCurrentStep()) return;

    try {
      setSubmitting(true);
      const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);

      // Tüm görünür soruları dahil et (cevap verilmemişleri de boş olarak)
      const answerPayload = survey.questions
        .filter((q) => isQuestionVisible(q))
        .map((q) => {
          const a = answers[q.id];
          return {
            question_id: q.id,
            answer_text: a?.text || null,
            answer_json: a?.json || null,
          };
        });

      const email = normalizeContactEmail(contactEmail);
      const name = (contactName || '').trim();
      const url = inviteToken ? `${API}/public/invite/${inviteToken}/submit` : `${API}/public/${slug}/submit`;
      const basePayload: Record<string, any> = {
        respondent_name: name || null,
        respondent_email: email || null,
        duration_seconds: duration,
        answers: answerPayload,
      };
      if (!inviteToken) basePayload.fingerprint_hash = fingerprintRef.current;

      try {
        await axios.post(url, basePayload);
      } catch (first: any) {
        // Sunucu e-postayı reddederse (eski sürüm: "respondent_email must be a valid email") yanıtı kaybetme:
        // e-postayı ada ekleyip e-postasız yeniden gönder.
        const msg = String(first?.response?.data?.message || '');
        if (first?.response?.status === 400 && email && /respondent_email|email/i.test(msg)) {
          await axios.post(url, {
            ...basePayload,
            respondent_email: null,
            respondent_name: name ? `${name} (e-posta: ${email})` : `(e-posta: ${email})`,
          });
        } else {
          throw first;
        }
      }

      // localStorage'a kaydet (dev ortamında atla)
      try {
        if (!isDev) localStorage.setItem(`survey_done_${inviteToken || slug}`, '1');
      } catch {
        /* ignore */
      }

      setCurrentStep(-1);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setCurrentStep(-1);
      } else {
        if (err?.response?.status === 429) {
          alert('Şu anda çok fazla gönderim var. Lütfen birkaç saniye sonra tekrar Gönder\'e basın; yanıtlarınız korunuyor.');
        } else {
          alert(friendlySubmitError(err?.response?.data?.message));
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Update answer ────────────────────────────────────
  const setAnswer = (questionId: number, text?: string, json?: any) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { text, json } }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  // ─── Render question input ────────────────────────────
  const renderQuestion = (q: Question) => {
    const answer = answers[q.id] || {};

    switch (q.question_type) {
      case 'short_text':
      case 'email':
      case 'number':
        return (
          <input
            type={q.question_type === 'email' ? 'email' : q.question_type === 'number' ? 'number' : 'text'}
            className="survey-input"
            style={styles.input}
            value={answer.text || ''}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            placeholder={q.question_type === 'email' ? 'ornek@email.com' : 'Cevabınız...'}
          />
        );

      case 'open_text':
        return <textarea className="survey-input" style={styles.textarea} value={answer.text || ''} onChange={(e) => setAnswer(q.id, e.target.value)} placeholder="Cevabınız..." />;

      case 'date':
        return <input type="date" className="survey-input" style={styles.input} value={answer.text || ''} onChange={(e) => setAnswer(q.id, e.target.value)} />;

      case 'single_choice':
        return (
          <div>
            {(q.options || []).map((opt, idx) => {
              const isSel = answer.text === opt;
              return (
                <button
                  key={idx}
                  type="button"
                  className="survey-option"
                  style={{ ...styles.optionBtn, ...(isSel ? styles.optionBtnSelected : {}) }}
                  onClick={() => setAnswer(q.id, opt)}
                >
                  <span
                    style={{
                      ...styles.optionRadio,
                      ...(isSel ? styles.optionRadioSelected : {}),
                    }}
                  >
                    {isSel && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'block' }} />}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
        );

      case 'multiple_choice':
        const selected: string[] = answer.json || [];
        return (
          <div>
            {(q.options || []).map((opt, idx) => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={idx}
                  type="button"
                  className="survey-option"
                  style={{ ...styles.optionBtn, ...(isSelected ? styles.optionBtnSelected : {}) }}
                  onClick={() => {
                    const newSel = isSelected ? selected.filter((s) => s !== opt) : [...selected, opt];
                    setAnswer(q.id, undefined, newSel);
                  }}
                >
                  <span
                    style={{
                      ...styles.optionCheck,
                      ...(isSelected ? styles.optionCheckSelected : {}),
                    }}
                  >
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
        );

      case 'rating':
        const ratingVal = answer.json?.value || 0;
        return (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', padding: '8px 0' }}>
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                type="button"
                className="survey-star"
                onClick={() => setAnswer(q.id, String(v), { value: v })}
                style={{
                  fontSize: '2.2rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: v <= ratingVal ? BRAND : '#d1d5db',
                  transition: 'all 0.2s cubic-bezier(.22,1,.36,1)',
                  padding: '4px',
                }}
              >
                ★
              </button>
            ))}
          </div>
        );

      default:
        return <input type="text" className="survey-input" style={styles.input} value={answer.text || ''} onChange={(e) => setAnswer(q.id, e.target.value)} />;
    }
  };

  // ─── Loading / Error states ───────────────────────────
  if (loading) {
    return (
      <div className="survey-page-wrapper" style={styles.wrapper}>
        <div style={{ ...styles.container, ...styles.centerText, padding: '60px 20px' }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: `3px solid ${BRAND_LIGHT}`,
              borderTopColor: BRAND,
              borderRadius: '50%',
              animation: 'surveySpin 0.7s linear infinite',
              margin: '0 auto 20px',
            }}
          />
          <p style={{ color: '#718096', fontSize: '0.95rem' }}>Anket yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="survey-page-wrapper" style={styles.wrapper}>
        <div style={{ ...styles.container, padding: '60px 32px', ...styles.centerText }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: BRAND_LIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke={BRAND} strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <h2 style={{ color: '#1e2a3a', marginBottom: 8, fontSize: '1.25rem', fontWeight: 700 }}>Anket Bulunamadı</h2>
          <p style={{ color: '#718096', fontSize: '0.95rem', lineHeight: 1.6 }}>{error || 'Bu anket mevcut değil veya artık aktif değil.'}</p>
        </div>
      </div>
    );
  }

  // Progress
  const progressPercent = currentStep <= 0 ? 0 : Math.min(100, (currentStep / totalSteps) * 100);
  const isContactStep = currentStep === stepNumbers.length + 1 && survey.collect_contact_info;
  const isLastQuestionStep = currentStep === stepNumbers.length && !survey.collect_contact_info;
  const isLastStep = isLastQuestionStep || isContactStep;

  return (
    <div className="survey-page-wrapper" style={{ ...styles.wrapper, ...(isIframe ? { background: 'transparent', padding: 0, minHeight: 'auto' } : {}) }}>
      <div className="survey-page-container" style={styles.container}>
        {/* Header */}
        <div className="survey-page-header" style={styles.header}>
          <div style={styles.headerDecor} />
          <div style={styles.headerDecor2} />
          <h1 style={styles.headerTitle}>{survey.title}</h1>
          {survey.event_name && <p style={styles.headerSub}>{survey.event_name}</p>}
          {currentStep > 0 && (
            <div style={styles.progressWrap}>
              <div style={{ ...styles.progressBar, width: `${progressPercent}%` }} />
            </div>
          )}
          {currentStep > 0 && currentStep <= totalSteps && (
            <p style={styles.stepIndicator}>
              Adım {currentStep} / {totalSteps}
            </p>
          )}
        </div>

        <div className="survey-page-content" style={styles.content}>
          {/* Welcome screen */}
          {currentStep === 0 && (
            <div className="survey-fadeIn" style={styles.centerText}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: BRAND_LIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    stroke={BRAND}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 style={{ color: '#1e2a3a', marginBottom: 12, fontWeight: 700, fontSize: '1.3rem' }}>{survey.welcome_title || 'Hoş Geldiniz!'}</h2>
              <p style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.7, maxWidth: 460, margin: '0 auto', whiteSpace: 'pre-line' }}>
                {survey.welcome_message || survey.description || 'Anketimize katılımınız için teşekkür ederiz.'}
              </p>
            </div>
          )}

          {/* Thank you screen */}
          {currentStep === -1 && (
            <div className="survey-fadeIn" style={styles.centerText}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: '#ecfdf5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 style={{ color: '#1e2a3a', marginBottom: 12, fontWeight: 700, fontSize: '1.3rem' }}>{survey.thank_you_title || 'Teşekkür Ederiz!'}</h2>
              <p style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.7, maxWidth: 460, margin: '0 auto' }}>
                {survey.thank_you_message || 'Yanıtınız başarıyla kaydedildi.'}
              </p>
              {showCalendar && calendar && (
                <div style={{ marginTop: 28, padding: '20px 18px', background: '#f8fafc', borderRadius: 14, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
                  <div style={{ fontWeight: 700, color: '#1e2a3a', marginBottom: 4 }}>Takviminize ekleyin</div>
                  <div style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 14 }}>
                    {new Intl.DateTimeFormat('tr-TR', { dateStyle: 'long', timeStyle: 'short', timeZone: 'Europe/Istanbul' }).format(new Date(calendar.start))}
                    {' – '}
                    {new Intl.DateTimeFormat('tr-TR', { timeStyle: 'short', timeZone: 'Europe/Istanbul' }).format(new Date(calendar.end))}
                    {calendar.location ? ` · ${calendar.location}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <a
                      href={googleCalendarUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'inline-block', background: '#0071e3', color: '#fff', textDecoration: 'none', padding: '11px 18px', borderRadius: 10, fontWeight: 600, fontSize: '0.92rem' }}
                    >
                      Google Takvim'e Ekle
                    </a>
                    <button
                      type="button"
                      onClick={downloadIcs}
                      style={{ background: '#1d1d1f', color: '#fff', border: 'none', padding: '11px 18px', borderRadius: 10, fontWeight: 600, fontSize: '0.92rem', cursor: 'pointer' }}
                    >
                      Apple / Outlook (.ics)
                    </button>
                  </div>
                  {calendar.send_email && (
                    <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: 12 }}>Takvim dosyası e-posta adresinize de gönderildi.</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Questions */}
          {currentStep > 0 && currentStep <= stepNumbers.length && (
            <div className="survey-fadeIn" key={`step-${currentStep}`}>
              {currentStepQuestions.map((q) => (
                <div key={q.id} className="survey-question-block" style={styles.questionBlock}>
                  <div style={styles.questionTitle}>
                    {q.question_text}
                    {q.is_required && <span style={{ color: BRAND, marginLeft: 4 }}>*</span>}
                  </div>
                  {renderQuestion(q)}
                  {validationErrors[q.id] && <p style={styles.error}>{validationErrors[q.id]}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Contact info step */}
          {isContactStep && (
            <div className="survey-fadeIn">
              <div style={styles.questionBlock}>
                <h3 style={{ ...styles.questionTitle, fontSize: '1.1rem', marginBottom: 20, color: BRAND_DARK, display: 'flex', alignItems: 'center' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginRight: 8 }}>
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" stroke={BRAND} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  İletişim Bilgileri
                </h3>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#374151', fontSize: '0.9rem' }}>Adınız</label>
                  <input
                    type="text"
                    className="survey-input"
                    style={styles.input}
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="İsim Soyisim"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#374151', fontSize: '0.9rem' }}>E-posta</label>
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="survey-input"
                    style={styles.input}
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      if (contactEmailWarning) setContactEmailWarning('');
                    }}
                    onBlur={() => {
                      const v = normalizeContactEmail(contactEmail);
                      setContactEmail(v);
                      setContactEmailWarning(emailLooksValid(v) ? '' : 'E-posta adresi eksik görünüyor (örn. ad@firma.com). İsterseniz bu şekilde de gönderebilirsiniz.');
                    }}
                    placeholder="ornek@email.com"
                  />
                  {contactEmailWarning && <p style={{ ...styles.error, color: '#b45309' }}>{contactEmailWarning}</p>}
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 6 }}>İsteğe bağlı. Yalnızca sizinle iletişime geçmek için kullanılır.</p>
                </div>
              </div>
            </div>
          )}

          {/* Honeypot (hidden) */}
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <input type="text" name="_website" tabIndex={-1} autoComplete="off" />
          </div>
        </div>

        {/* Footer */}
        {currentStep !== -1 && (
          <div className="survey-page-footer" style={styles.footer}>
            {currentStep > 0 ? (
              <button type="button" className="survey-btn-secondary" style={styles.btnSecondary} onClick={handleBack}>
                ← Geri
              </button>
            ) : (
              <div />
            )}
            <button type="button" className="survey-btn-primary" style={{ ...styles.btnPrimary, opacity: submitting ? 0.7 : 1 }} onClick={handleNext} disabled={submitting}>
              {submitting ? 'Gönderiliyor...' : currentStep === 0 ? 'Başla' : isLastStep ? 'Gönder' : 'İleri →'}
            </button>
          </div>
        )}

        {/* Powered by */}
        <div style={styles.poweredBy}>
          <span style={{ fontSize: '0.75rem', color: '#a0aec0' }}>
            Powered by{' '}
            <a href="https://www.aynetteknoloji.com.tr" target="_blank" rel="noopener noreferrer" style={{ color: BRAND, fontWeight: 600, textDecoration: 'none' }}>
              Aynet Teknoloji
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
